import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { t } from "../i18n";

/**
 * Drop-in replacement for native <select> in the panel's design: the field is
 * a quiet well like the search in Messages, the list is a dashboard card with
 * plain rows, and the chosen row is marked with the accent and a check.
 * Accepts a normalized `options` array so call sites stay short:
 *
 *   <StyledSelect
 *     value={typeValue}
 *     onChange={setTypeValue}
 *     placeholder="Тип цены"
 *     options={[
 *       { value: "FIXED", label: "Фиксированная" },
 *       { value: "NEGOTIABLE", label: "Договорная" },
 *     ]}
 *   />
 *
 * Each option may include a `subtitle` (a muted second line) and a `disabled`
 * flag. Colours and geometry live in index.css (.ui-select*), so every theme
 * gets them from its tokens; the field height and radius can be matched to
 * neighbouring inputs with --ui-select-h / --ui-select-r.
 *
 * The list is portalled to <body> and placed with fixed coordinates: inside a
 * modal it used to be cut off by the dialog's own edge.
 *
 * `searchable` adds a filter field on top of the list (off by default, so
 * every existing select is unchanged). It matches the label, the subtitle and
 * an optional per-option `keywords` string — text that should be findable but
 * not shown, e.g. an id or a Telegram handle.
 */

// Высота строки и отступы списка — для прикидки, хватит ли места снизу.
const OPTION_ROW_PX = 36;
// Высота поля поиска над списком (searchable).
const SEARCH_ROW_PX = 44;
const MENU_MAX_PX = 280;
const MENU_GAP_PX = 6;
const VIEWPORT_MARGIN_PX = 8;

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ChevronIcon = () => (
  <svg className="ui-select-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

let selectIdSeq = 0;

// Совпадение с запросом поиска: подпись, вторая строка и скрытые keywords.
// needle — уже в нижнем регистре и без краевых пробелов.
const optionMatches = (opt, needle) => [
  typeof opt.label === "string" ? opt.label : "",
  typeof opt.subtitle === "string" ? opt.subtitle : "",
  opt.keywords
].join(" ").toLowerCase().includes(needle);

const StyledSelect = ({
  value,
  onChange,
  options = [],
  placeholder = t("Выберите…"),
  emptyText = t("Нет доступных вариантов"),
  disabled = false,
  size = "md",                // "sm" | "md"
  fullWidth = true,
  style = {},
  popupStyle = {},
  ariaLabel = "",
  onOpenChange,
  searchable = false,
  // Список не уже этого (px), даже если поле узкое — например, подогнано под
  // короткое значение.
  menuMinWidth = 0,
  searchPlaceholder = t("Поиск"),
  noMatchesText = t("Ничего не найдено"),
}) => {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [query, setQuery] = useState("");
  const [listId] = useState(() => `ui-select-${++selectIdSeq}`);

  const normalizedOptions = useMemo(() => (Array.isArray(options) ? options : []).map((opt) => {
    if (opt == null) return null;
    if (typeof opt === "string") return { value: opt, label: opt, keywords: "" };
    return {
      value: opt.value,
      label: opt.label != null ? opt.label : String(opt.value ?? ""),
      subtitle: opt.subtitle || "",
      keywords: String(opt.keywords || ""),
      disabled: Boolean(opt.disabled),
    };
  }).filter(Boolean), [options]);

  // Что показано в списке. Без поиска — все варианты, и всё ниже (стрелки,
  // подсветка, выбранная строка) работает по этому же списку.
  const visibleOptions = useMemo(() => {
    const needle = searchable ? query.trim().toLowerCase() : "";
    if (!needle) return normalizedOptions;
    return normalizedOptions.filter((opt) => optionMatches(opt, needle));
  }, [normalizedOptions, query, searchable]);

  const selectedIndex = useMemo(
    () => visibleOptions.findIndex((opt) => String(opt.value) === String(value)),
    [visibleOptions, value]
  );
  // Подпись в поле — по всему списку: выбранный может быть скрыт фильтром.
  const selected = useMemo(
    () => normalizedOptions.find((opt) => String(opt.value) === String(value)) || null,
    [normalizedOptions, value]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (wrapRef.current && wrapRef.current.contains(event.target)) return;
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      setOpen(false);
    };
    const onKey = (event) => {
      // preventDefault: этот Escape закрывает только список, а не окно с ним
      // (см. utils/escapeLayers.js).
      if (event.key === "Escape") { event.preventDefault(); setOpen(false); }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (typeof onOpenChange === "function") onOpenChange(open);
  }, [open, onOpenChange]);

  // Список стоит под полем, а если снизу тесно (поле у низа окна) и сверху
  // просторнее — над ним. Меряется до отрисовки, чтобы список не мелькнул не
  // там; при прокрутке страницы или окна под ним список закрывается, как
  // родной <select>, а при смене размера окна — переставляется.
  useLayoutEffect(() => {
    if (!open || typeof window === "undefined") return undefined;
    const place = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      // Высота — по полному списку, а не по отфильтрованному: иначе список
      // прыгал бы вверх-вниз на каждую набранную букву.
      const wanted = Math.min(
        MENU_MAX_PX,
        Math.max(1, normalizedOptions.length) * OPTION_ROW_PX + 10 + (searchable ? SEARCH_ROW_PX : 0)
      );
      const below = viewportH - rect.bottom - MENU_GAP_PX - VIEWPORT_MARGIN_PX;
      const above = rect.top - MENU_GAP_PX - VIEWPORT_MARGIN_PX;
      const up = below < wanted && above > below;
      const width = Math.min(Math.max(rect.width, menuMinWidth), viewportW - VIEWPORT_MARGIN_PX * 2);
      setPlacement({
        up,
        left: Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.left, viewportW - width - VIEWPORT_MARGIN_PX)),
        width,
        top: up ? undefined : rect.bottom + MENU_GAP_PX,
        bottom: up ? viewportH - rect.top + MENU_GAP_PX : undefined,
        maxHeight: Math.max(120, Math.min(MENU_MAX_PX, up ? above : below)),
      });
    };
    place();
    const onScroll = (event) => {
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) return;
      // Пока набирают запрос, прокрутка — чаще всего не жест человека: на
      // телефоне её делает сам браузер, поднимая клавиатуру под поле поиска.
      // Закрыть список тут значило бы закрыть его, едва он открылся, —
      // поэтому он переставляется к полю, а не закрывается.
      if (searchRef.current && document.activeElement === searchRef.current) {
        place();
        return;
      }
      setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, normalizedOptions.length, searchable, menuMinWidth]);

  // Запрос живёт, пока список открыт: открыли заново — снова весь список.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // Поле поиска получает фокус, как только список встал на место.
  // preventScroll — фокус не должен листать страницу: это закрыло бы список.
  useEffect(() => {
    if (!open || !searchable || !placement || !searchRef.current) return;
    if (document.activeElement !== searchRef.current) searchRef.current.focus({ preventScroll: true });
  }, [open, searchable, placement]);

  // Подсвеченная стрелками строка держится в видимой части списка. Прокрутка
  // своя, не scrollIntoView: тот листает и страницу, а это закрыло бы список.
  useEffect(() => {
    const menu = menuRef.current;
    if (!open || activeIndex < 0 || !menu) return;
    const row = menu.querySelector(`[data-index="${activeIndex}"]`);
    if (!row) return;
    if (row.offsetTop < menu.scrollTop) menu.scrollTop = row.offsetTop - 4;
    else if (row.offsetTop + row.offsetHeight > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = row.offsetTop + row.offsetHeight - menu.clientHeight + 4;
    }
  }, [open, activeIndex, placement]);

  const firstEnabledFrom = (start, step, list = visibleOptions) => {
    const count = list.length;
    for (let i = 0, index = start; i < count; i += 1, index += step) {
      const wrapped = ((index % count) + count) % count;
      if (!list[wrapped].disabled) return wrapped;
    }
    return -1;
  };

  const openMenu = () => {
    if (disabled) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstEnabledFrom(0, 1));
    setOpen(true);
  };

  // Фокус возвращается на поле: при поиске он был в строке запроса, которая
  // исчезает вместе со списком, и иначе ушёл бы в никуда.
  const closeMenu = () => {
    setOpen(false);
    if (searchable && triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  };

  const choose = (opt) => {
    if (!opt || opt.disabled) return;
    if (typeof onChange === "function") onChange(opt.value);
    closeMenu();
  };

  // Новый запрос — подсветка на первом найденном: Enter сразу выбирает его.
  const onQueryChange = (next) => {
    setQuery(next);
    const needle = next.trim().toLowerCase();
    const list = needle ? normalizedOptions.filter((opt) => optionMatches(opt, needle)) : normalizedOptions;
    setActiveIndex(firstEnabledFrom(0, 1, list));
  };

  // Стрелки, Home/End и Enter — общие для кнопки поля и строки поиска.
  // Пробел из строки поиска — это пробел в запросе, а не выбор.
  const handleListKeys = (event, fromSearch = false) => {
    const { key } = event;
    if (!visibleOptions.length) return;
    if (key === "ArrowDown") { event.preventDefault(); setActiveIndex((prev) => firstEnabledFrom(prev + 1, 1)); }
    else if (key === "ArrowUp") { event.preventDefault(); setActiveIndex((prev) => firstEnabledFrom((prev < 0 ? 0 : prev) - 1, -1)); }
    else if (key === "Home" && !fromSearch) { event.preventDefault(); setActiveIndex(firstEnabledFrom(0, 1)); }
    else if (key === "End" && !fromSearch) { event.preventDefault(); setActiveIndex(firstEnabledFrom(visibleOptions.length - 1, -1)); }
    else if (key === "Enter" || (key === " " && !fromSearch)) { event.preventDefault(); choose(visibleOptions[activeIndex]); }
  };

  const onTriggerKeyDown = (event) => {
    if (disabled) return;
    const { key } = event;
    if (!open) {
      if (key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === " ") {
        event.preventDefault();
        openMenu();
      }
      return;
    }
    if (key === "Tab") { setOpen(false); return; }
    handleListKeys(event);
  };

  const onSearchKeyDown = (event) => {
    if (event.key === "Tab") { setOpen(false); return; }
    handleListKeys(event, true);
  };

  const className = [
    "ui-select",
    size === "sm" ? "ui-select--sm" : "",
    open ? "is-open" : "",
    disabled ? "is-disabled" : "",
  ].filter(Boolean).join(" ");

  return (
    <div ref={wrapRef} className={className} style={{ width: fullWidth ? "100%" : "auto", ...style }}>
      <button
        ref={triggerRef}
        type="button"
        className="ui-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        aria-label={ariaLabel || placeholder}
        disabled={disabled}
        onClick={() => { if (open) setOpen(false); else openMenu(); }}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={`ui-select-value${selected ? "" : " is-placeholder"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronIcon />
      </button>

      {open && placement && typeof document !== "undefined" && createPortal(
        // data-modal-scroll — чтобы список листался внутри окон на телефоне:
        // их защита жестов пропускает только помеченные области.
        <div
          ref={menuRef}
          id={listId}
          role="listbox"
          data-modal-scroll="1"
          className={`ui-select-menu is-floating${placement.up ? " is-up" : ""}`}
          style={{
            left: `${placement.left}px`,
            width: `${placement.width}px`,
            top: placement.top != null ? `${placement.top}px` : "auto",
            bottom: placement.bottom != null ? `${placement.bottom}px` : "auto",
            maxHeight: `${placement.maxHeight}px`,
            ...popupStyle
          }}
        >
          {searchable && normalizedOptions.length > 0 && (
            <div className="ui-select-search">
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                aria-controls={listId}
                aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
          {normalizedOptions.length === 0 ? (
            <div className="ui-select-empty">{emptyText}</div>
          ) : visibleOptions.length === 0 ? (
            <div className="ui-select-empty">{noMatchesText}</div>
          ) : visibleOptions.map((opt, index) => {
            const isSelected = index === selectedIndex;
            const optionClass = [
              "ui-select-option",
              isSelected ? "is-selected" : "",
              index === activeIndex ? "is-active" : "",
            ].filter(Boolean).join(" ");
            return (
              <button
                key={String(opt.value)}
                id={`${listId}-${index}`}
                data-index={index}
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={isSelected}
                disabled={opt.disabled}
                className={optionClass}
                onMouseMove={() => { if (!opt.disabled && index !== activeIndex) setActiveIndex(index); }}
                onClick={() => choose(opt)}
              >
                <span className="ui-select-option-text">
                  <span className="ui-select-option-label">{opt.label}</span>
                  {opt.subtitle && <span className="ui-select-option-sub">{opt.subtitle}</span>}
                </span>
                {isSelected && <span className="ui-select-check"><CheckIcon /></span>}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

export default StyledSelect;
